import React, { useRef } from "react";
import { Box, Chip, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const inlineInputSx = (theme) => ({
  border: "none",
  outline: "none",
  background: "transparent",
  width: "100%",
  minWidth: 0,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.35,
  fontFamily: "inherit",
  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#171717",
  padding: 0,
  "&::placeholder": {
    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.45)" : "rgba(88,89,91,0.55)",
    opacity: 1,
  },
});

/**
 * Single multi-value filter input (V4+).
 *
 * States (same field throughout):
 * 1. Default — placeholder only
 * 2. Open / focused — active border + caret; typing filters options when wired
 * 3. Selected — chips in one horizontal scrolling row + same input for more search/entry
 *
 * Clear (X) is fixed on the right whenever values exist. No expand chevron.
 */
export default function SelectedChipsArea({
  values = [],
  getLabel = (value) => String(value ?? ""),
  getKey = (value, index) => `${getLabel(value)}-${index}`,
  onRemove,
  onClear,
  disabled = false,
  inputValue = "",
  onInputChange,
  onInputKeyDown,
  onInputBlur,
  onInputFocus,
  onInputClick,
  placeholder = "Search...",
  autoFocus = false,
  inputId,
  inputRef: inputRefProp,
  shellSx,
  inputError = false,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-expanded": ariaExpanded,
  "aria-controls": ariaControls,
  "data-testid": testId = "selected-chips-area",
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const localInputRef = useRef(null);
  const inputRef = inputRefProp || localInputRef;
  const items = Array.isArray(values) ? values : [];
  const hasValues = items.length > 0;
  const hasInputError = inputError || ariaInvalid === true || ariaInvalid === "true";

  const focusInput = () => {
    if (disabled) return;
    inputRef.current?.focus?.();
  };

  return (
    <Box
      data-testid={testId}
      onMouseDown={(event) => {
        // Keep focus on the single input when clicking the shell (not chip delete / clear).
        if (event.target.closest("[data-chip-control='true']")) return;
        focusInput();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        minHeight: 40,
        borderRadius: "10px",
        border: `1px solid ${
          hasInputError
            ? theme.palette.error.main
            : isDark
              ? "rgba(255,255,255,0.1)"
              : "rgba(88,89,91,0.14)"
        }`,
        backgroundColor: isDark ? "#161616" : "#F7F7F8",
        cursor: disabled ? "default" : "text",
        transition: "background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:focus-within": {
          backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
          borderColor: hasInputError
            ? theme.palette.error.main
            : theme.palette.primary.main,
          boxShadow: hasInputError
            ? `0 0 0 3px ${theme.palette.error.main}1F`
            : isDark
              ? `0 0 0 3px ${theme.palette.primary.main}59`
              : `0 0 0 3px ${theme.palette.primary.main}1F`,
        },
        opacity: disabled ? 0.55 : 1,
        pointerEvents: disabled ? "none" : "auto",
        ...shellSx,
      }}
    >
      <Box
        data-testid={`${testId}-chips`}
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: "6px",
          py: "6px",
          pl: "10px",
          pr: "4px",
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: isDark
            ? "rgba(255,255,255,0.25) transparent"
            : "rgba(88,89,91,0.28) transparent",
          "&::-webkit-scrollbar": { height: 5 },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 999,
            backgroundColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(88,89,91,0.28)",
          },
        }}
      >
        {items.map((value, index) => {
          const label = getLabel(value);
          return (
            <Chip
              key={getKey(value, index)}
              size="small"
              label={label}
              data-chip-control="true"
              onDelete={
                typeof onRemove === "function"
                  ? () => onRemove(value, index)
                  : undefined
              }
              deleteIcon={
                <CloseIcon
                  fontSize="small"
                  data-chip-control="true"
                  role="button"
                  aria-label={`Remove ${label}`}
                />
              }
              sx={{
                flex: "0 0 auto",
                maxWidth: 220,
                height: 26,
                borderRadius: "999px",
                backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#EDEDEF",
                color: isDark ? "#FFFFFF" : "#171717",
                fontSize: 12,
                fontWeight: 550,
                "& .MuiChip-label": {
                  px: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
                "& .MuiChip-deleteIcon": {
                  fontSize: 16,
                  color: isDark ? "rgba(255,255,255,0.55)" : "rgba(88,89,91,0.55)",
                  "&:hover": {
                    color: isDark ? "#FFFFFF" : "#171717",
                  },
                },
              }}
            />
          );
        })}

        <Box
          sx={{
            flex: hasValues ? "0 0 auto" : "1 1 auto",
            minWidth: hasValues ? 72 : 0,
            width: hasValues ? Math.max(72, String(inputValue || "").length * 8 + 24) : "100%",
            maxWidth: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            component="input"
            id={inputId}
            ref={inputRef}
            value={inputValue}
            disabled={disabled}
            autoFocus={autoFocus}
            placeholder={hasValues ? "" : placeholder}
            aria-label={placeholder}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            aria-expanded={ariaExpanded}
            aria-controls={ariaControls}
            aria-autocomplete="list"
            role="combobox"
            onChange={(event) => onInputChange?.(event.target.value)}
            onBlur={onInputBlur}
            onFocus={onInputFocus}
            onClick={onInputClick}
            onKeyDown={onInputKeyDown}
            sx={inlineInputSx(theme)}
          />
        </Box>
      </Box>

      {hasValues ? (
        <Box
          data-testid={`${testId}-controls`}
          data-chip-control="true"
          sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "stretch",
            py: 0.25,
            pr: 0.5,
            pl: 0.25,
            borderLeft: `1px solid ${
              isDark ? "rgba(255,255,255,0.08)" : "rgba(88,89,91,0.1)"
            }`,
          }}
        >
          <IconButton
            type="button"
            size="small"
            data-chip-control="true"
            aria-label="Clear selected values"
            disabled={disabled || typeof onClear !== "function"}
            onClick={onClear}
            sx={{
              p: "4px",
              color: isDark ? "rgba(255,255,255,0.55)" : "rgba(88,89,91,0.55)",
              "&:hover": {
                color: "#D80000",
                backgroundColor: "transparent",
              },
              "&.Mui-disabled": {
                color: isDark ? "rgba(255,255,255,0.2)" : "rgba(88,89,91,0.25)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ) : null}
    </Box>
  );
}
